import { createIntl, createIntlCache } from 'react-intl';

import Cookie from 'js-cookie';
import moment from 'moment';

import { COOKIE_AST_APP, COOKIE_AST_WEBAPP_I18N, COOKIE_LANG, COOKIE_DEVICE_INFO, COOKIE_AST_TOKEN_JS } from '../constants/Common';
import { getLocaleInfo } from './Lang/getLocaleInfo';

/** `intl` memoizado junto com o `localeInfo` que o gerou — ver `getIntl`. */
let intlMemo = null;

const Utils = {
    /**
     * Return um,uma
     * @param gender
     * @param moreThanOne
     * @returns {*}
     */
    indefiniteArticle(gender = 'female', moreThanOne = false) {
        switch (gender) {
            case 'female':
                return moreThanOne ? 'duas' : 'uma';
            case 'F':
                return moreThanOne ? 'duas' : 'uma';
            case 'male':
                return moreThanOne ? 'dois' : 'um';
            case 'M':
                return moreThanOne ? 'dois' : 'um';
            default:
                return moreThanOne ? 'duas' : 'uma';
        }
    },

    isApp() {
        return !!Cookie.get(COOKIE_AST_APP);
    },

    isI18nWebapp() {
        return !!Cookie.get(COOKIE_AST_WEBAPP_I18N);
    },

    isSafari() {
        return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    },

    normalizeHTML(html) {
        const normalize = html.replace(/&quot;/g, '"');
        return normalize.replace(/&#039;/g, '\'');
    },

    bindHtml(text) {
        return { __html: text };
    },

    bindPercentage(value) {
        return `${value}%`;
    },

    bindPixel(value) {
        return `${value}px`;
    },

    isInternational() {
        const lang = Cookie.get(COOKIE_LANG);
        if (!lang) return false;

        if (lang !== 'pt_br') return true;

        return false;
    },

    isUsInternational() {
        const lang = Cookie.get(COOKIE_LANG);
        if (!lang) return false;

        return (lang === 'en_us');
    },

    isEsInternational() {
        const lang = Cookie.get(COOKIE_LANG);
        if (!lang) return false;

        return (lang === 'es');
    },

    isFrInternational() {
        const lang = Cookie.get(COOKIE_LANG);
        if (!lang) return false;

        return (lang === 'fr');
    },

    isItInternational() {
        const lang = Cookie.get(COOKIE_LANG);
        if (!lang) return false;

        return (lang === 'it');
    },

    isDeInternational() {
        const lang = Cookie.get(COOKIE_LANG);
        if (!lang) return false;

        return (lang === 'de');
    },

    isInternationalInProgress() {
        return Utils.isFrInternational() || Utils.isItInternational() || Utils.isDeInternational();
    },

    getLocale() {
        return (navigator.languages && navigator.languages[0]) || navigator.language || navigator.userLanguage || 'pt-BR';
    },

    getLang() {
        return Cookie.get(COOKIE_LANG) || 'pt_br';
    },

    flattenMessages(nestedMessages, prefix = '') {
        if (!nestedMessages) return null;

        return Object.keys(nestedMessages).reduce((messages, key) => {
            const value = nestedMessages[key];
            const prefixedKey = prefix ? `${prefix}.${key}` : key;
            const result = messages;

            if (typeof value === 'string') {
                result[prefixedKey] = value;
            } else {
                Object.assign(result, this.flattenMessages(value, prefixedKey));
            }
            return result;
        }, {});
    },

    formatDateToCurrentLocale(
        date = new Date(),
        locale = (Cookie.get(COOKIE_LANG) || 'pt-BR')
    ) {
        if (locale.includes('_')) {
            locale = locale.replace('_', '-').toLowerCase();
        }

        const dateOptions = {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        };

        const hourOptions = {
            hour: '2-digit',
            minute: '2-digit',
        };

        if (locale === 'en-US') {
            hourOptions.hour12 = true;
        }

        const formattedDate = date.toLocaleDateString(locale, dateOptions).replace(/\./g, '/');
        const formattedHour = date.toLocaleTimeString(locale, hourOptions);

        return { date: formattedDate, hour: formattedHour };
    },

    shuffle(array) {
        const newArray = [...array];
        for (let currentIndex = (newArray.length - 1); currentIndex > 0; currentIndex--) {
            const randomIndex = Math.floor(Math.random() * currentIndex);
            const temp = newArray[currentIndex];
            newArray[currentIndex] = Object.assign(newArray[randomIndex]);
            newArray[randomIndex] = temp;
        }

        return [...newArray];
    },

    smoothScroll(destination = 0, parent, time = 15) {
        const init = () => {
            const start = parent ? parent.scrollTop : window.scrollY;
            const ticks = time;
            let i = 0;
            const positionY = () => (destination - start) * i / ticks + start;
            const isFinished = () => i++ >= ticks;
            return { positionY, isFinished };
        };

        const shouldScroll = () => {
            if (scroll.isFinished()) return;
            if (parent) parent.scrollTop = scroll.positionY();
            else window.scrollY = scroll.positionY();

            requestAnimationFrame(shouldScroll);
        };

        let scroll = init();
        requestAnimationFrame(shouldScroll);
    },

    getActualYear() {
        return new Date().getFullYear();
    },

    /**
     * Memoizado pela identidade do `localeInfo`: ela troca quando o `import()` do locale
     * substitui o catálogo, e só aí o provider é refeito. Sem o memo, cada chamada repete o
     * `flattenMessages` sobre o catálogo inteiro.
     */
    getIntl() {
        const localeInfo = getLocaleInfo();

        if (intlMemo && intlMemo.localeInfo === localeInfo) return intlMemo.intl;

        const intl = createIntl(
            { locale: localeInfo.locale, messages: this.flattenMessages(localeInfo) },
            createIntlCache()
        );

        intlMemo = { localeInfo, intl };

        return intl;
    },

    romanize(num) {
        if (isNaN(num) || num === 0) return 0;
        const digits = String(+num).split('');
        const key = ['', 'C', 'CC', 'CCC', 'CD', 'D', 'DC', 'DCC', 'DCCC', 'CM',
            '', 'X', 'XX', 'XXX', 'XL', 'L', 'LX', 'LXX', 'LXXX', 'XC',
            '', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX'];
        let roman = '';
        let i = 3;
        while (i--) roman = (key[+digits.pop() + (i * 10)] || '') + roman;
        return Array(+digits.join('') + 1).join('M') + roman;
    },

    randowId() {
        return Math.floor((1 + Math.random()) * 0x10000)
            .toString(16)
            .substring(1);
    },

    moveIndexInArray(arr, fromIndex, toIndex) {
        const neoArr = Array.from(arr);
        const element = neoArr[fromIndex];
        neoArr.splice(fromIndex, 1);
        neoArr.splice(toIndex, 0, element);

        return neoArr;
    },

    isIos() {
        if (this.isApp() && Cookie.get(COOKIE_DEVICE_INFO)) {
            const { system } = JSON.parse(Cookie.get(COOKIE_DEVICE_INFO));
            return /OS/ig.test(system);
        }

        const userAgent = navigator.userAgent || navigator.vendor || window.opera;

        if (/iPhone|iPad|iPod/i.test(userAgent)) {
            return true;
        }

        const isMacTouch = /Macintosh/i.test(userAgent) && navigator.maxTouchPoints && navigator.maxTouchPoints > 1;
        if (isMacTouch) {
            return true;
        }

        return false;
    },

    setWVmessageListenner(listener = (event) => { }) {
        if (this.isIos()) window.addEventListener('message', listener);
        else document.addEventListener('message', listener);
    },

    convertHourToUs(hour) {
        if (!hour) return '';
        if (!this.isUsInternational()) return hour;
        return moment(hour, 'hh:mm').format('hh:mm A');
    },

    getUUID() {
        const getRandomSymbol = (symbol) => {
            let array;

            if (symbol === 'y') {
                array = ['8', '9', 'a', 'b'];
                const random = window.crypto.getRandomValues(new Uint32Array(1))[0];
                return array[Math.floor(random * array.length)];
            }

            array = new Uint8Array(1);
            window.crypto.getRandomValues(array);
            return (array[0] % 16).toString(16);
        };

        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, getRandomSymbol);
    },

    getJWT() {
        return Cookie.get(COOKIE_AST_TOKEN_JS);
    },

    isLoggedIn() {
        return !!this.getJWT();
    },

    neoSmoothScroll(targetPosition, duration, targetElement) {
        const startPosition = window.pageYOffset;
        let targetOffset = 0;

        if (targetElement) {
            const elementRect = targetElement.getBoundingClientRect();
            targetOffset = elementRect.top + startPosition;
        }

        const distance = targetPosition - startPosition - targetOffset;
        const startTime = performance.now();

        function easeInOutQuad(t, b, c, d) {
            t /= d / 2;
            if (t < 1) return (c / 2) * t * t + b;
            t--;
            return (-c / 2) * (t * (t - 2) - 1) + b;
        }

        function scroll() {
            const currentTime = performance.now();
            const timeElapsed = currentTime - startTime;

            window.scrollTo(0, easeInOutQuad(timeElapsed, startPosition, distance + targetOffset, duration));

            if (timeElapsed < duration) {
                requestAnimationFrame(scroll);
            }
        }

        scroll();
    },

    removeUnwantedAds(shouldShowAds) {
        if (shouldShowAds) return;

        const body = document.body;
        const selector = 'ins.adsbygoogle.adsbygoogle-noablate';

        const observer = new MutationObserver(() => {
            const adsToRemove = document.querySelectorAll(selector);

            adsToRemove.forEach((ad) => {
                const htmlAd = ad;
                if ((htmlAd.hasAttribute('data-anchor-shown') && htmlAd.getAttribute('data-anchor-shown') === 'true') ||
                    (htmlAd.hasAttribute('data-ad-status') && htmlAd.getAttribute('data-ad-status') === 'unfilled') ||
                    !htmlAd.hasAttribute('data-ad-slot')) {
                    htmlAd.remove();
                }
            });
        });

        observer.observe(body, { attributes: true, attributeFilter: ['style'] });

        return () => {
            observer.disconnect();
        };
    },
};

export default Utils;